import React from 'react';
import { format } from 'date-fns';
import user_logo from 'assets/icons/user.svg';
import { IconEdit } from '@tabler/icons-react';
import { Attendances } from './AttendanceField';
import { useTranslation } from 'react-i18next';

interface AttendanceTableProps {
  attendances: Attendances[];
  onEdit: (attendanceId: string) => void;
}

const AttendanceTable: React.FC<AttendanceTableProps> = ({
  attendances,
  onEdit,
}) => {
  const { t } = useTranslation('attendance');
  const formatDate = (initialDate: any) => {
    let newDate = format(new Date(initialDate), t('table.date-format'));
    return newDate;
  };

  const formatTime = (time: string) => {
    const [hrs, mins] = time.split(':');
    const hours = parseInt(hrs, 10);
    const minutes = parseInt(mins, 10);

    return hours === 0
      ? `${minutes} ${t('table.mins')}`
      : `${hours} ${t('table.hours')} ${minutes} ${t('table.mins')}`;
  };

  function calculateDuration(
    check_in: string | Date,
    check_out: string | Date
  ): string {
    // Calculate the difference in milliseconds
    const checkIn = new Date(check_in);
    const checkOut = new Date(check_out);

    const durationMs = checkOut.getTime() - checkIn.getTime();

    // Convert milliseconds to hours, minutes, and seconds
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    // Return the duration as a formatted string
    if (durationMs < 60000) {
      return `0 ${t('table.mins')}`;
    } else if (hours === 0) {
      return minutes === 0 ? '-' : `${minutes} ${t('table.mins')}`;
    } else {
      return minutes === 0
        ? `${hours} ${t('table.hours')}`
        : `${hours} ${t('table.hours')} ${minutes} ${t('table.mins')}`;
    }
  }

  let lastDisplayedDriver: { name: string; phone: string } | null = null;

  return (
    <main className="flex flex-col my-6">
      <div className="grid grid-cols-11 gap-4 px-2 py-2.5 bg-primary/50 rounded-md w-full text-caption">
        <div className="col-span-2">{t('table.driver-name')}</div>
        <div className="col-span-2">{t('table.checkin')}</div>
        <div className="col-span-2">{t('table.checkout')}</div>
        <div className="col-span-2 text-center">{t('table.duration')}</div>
        <div className="col-span-2 text-center">{t('table.overtime')}</div>
        <div className="col-span-1 text-center">{t('table.action')}</div>
      </div>
      {attendances.length === 0 ? (
        <div className="text-center my-4">{t('table.no-data')}</div>
      ) : (
        attendances.map((attendance, index) => {
          const displayDriver =
            !lastDisplayedDriver ||
            lastDisplayedDriver.name !== attendance.user.name ||
            lastDisplayedDriver.phone !== attendance.user.phone;

          if (displayDriver) {
            lastDisplayedDriver = {
              name: attendance.user.name,
              phone: attendance.user.phone,
            };
          }

          return (
            <div
              key={index}
              className="grid grid-cols-11 gap-4 px-2 sm:h-8 md:h-12 lg:h-16 border-b text-caption"
            >
              <div className="col-span-2 flex space-x-2 items-center">
                {displayDriver && (
                  <>
                    <img
                      src={attendance.user?.profile_picture || user_logo}
                      alt="User"
                      className="h-10 w-10 object-cover rounded-full"
                    />
                    <div className="w-full truncate">
                      <div className="text-body-2 text-neutral/300 truncate">
                        {attendance.user?.name || t('table.no-data')}
                      </div>
                      <div className="text-caption text-neutral/100 truncate">
                        {attendance.user?.phone ||
                          attendance.user?.email ||
                          'N/A'}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="col-span-2 flex items-center">
                {formatDate(attendance.check_in)}
              </div>
              <div className="col-span-2 flex items-center">
                {attendance.check_out ? formatDate(attendance.check_out) : '-'}
              </div>

              <div className="col-span-2 flex items-center justify-center text-center">
                {calculateDuration(attendance.check_in, attendance.check_out)}
              </div>

              <div
                className={`col-span-2 flex items-center justify-center text-center
                ${
                  attendance.overtime_duration
                    ? 'text-error'
                    : 'text-neutral/300'
                }
              `}
              >
                {attendance.overtime_duration
                  ? formatTime(attendance.overtime_duration)
                  : '-'}
              </div>
              <div className="col-span-1 flex items-center justify-center gap-3 cursor-pointer">
                <IconEdit
                  stroke={1.5}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(attendance.id);
                  }}
                  className="text-neutral/100 size-5"
                />
              </div>
            </div>
          );
        })
      )}
    </main>
  );
};
export default AttendanceTable;
